import { useCallback } from 'react';
// This should be the only file using the useNavigate hook directly
// eslint-disable-next-line no-restricted-imports
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { matchRoutes, To } from '@remix-run/router';
import type { NavigateOptions } from 'react-router';
import { router as appRouter } from '../../containers/App';

const isToExternalHost = (to: string): boolean => {
  try {
    const url = new URL(to);
    return url.hostname !== window.location.hostname;
  } catch (e) {
    // "to" can also validly be a path without a hostname
    return false;
  }
};

// react-router breaks if it is provided with domain information (e.g. https://id.atlassian.com:443)
// so we need to remove it
const removeDomainInformation = (to: string): string => {
  let pathname: string;
  try {
    const url = new URL(to);
    pathname = url.pathname + url.search + url.hash;
  } catch (e) {
    // "to" can also validly be a path without a hostname
    pathname = to;
  }

  return pathname;
};

// this is needed because the parameters of Partial<Path> are a subset of URL, but if we
// pass a URL to react-router's navigate function, it may break.
const stringifyIfUrl = (to: number | To | URL): number | To => {
  if (to instanceof URL) {
    return to.toString();
  }
  return to;
};

/**
 * This hook is a wrapper around react-router's useNavigate that will use soft navigation if possible,
 * otherwise will fall back to a hard redirect using window.location.assign.
 */
export const useNavigateSoftOrHard = () => {
  const softNavigate = useNavigate();

  return useCallback<NavigateFunction>(
    (to: number | To | URL, options?: NavigateOptions) => {
      const destination = stringifyIfUrl(to);

      if (typeof destination === 'number') {
        return softNavigate(destination);
      }

      if (typeof destination !== 'string') {
        // destination is a Partial<Path>
        return softNavigate(destination, options);
      }

      if (options) {
        return softNavigate(destination, options);
      }

      if (isToExternalHost(destination)) {
        return window.location.assign(destination);
      }

      const pathname = removeDomainInformation(destination);

      // if the pathname matches the frontend App router then we can soft navigate
      const routeMatches = matchRoutes(appRouter.routes, pathname);
      if (routeMatches && routeMatches.length > 0) {
        return softNavigate(pathname);
      }

      return window.location.assign(destination);
    },
    [softNavigate]
  );
};
